import { selectStandardProps } from "@/utils/componentUtils";
import { Form, Select } from "antd";
import { FormItemProps } from "antd/lib/form";
import { SelectProps } from "antd/lib/select";
import groupBy from "lodash/groupBy";
import { DEFAULT_LOCALE } from "../../../../app/i18n";
import { useEnumeration } from "../../../../common/api/queries";
import type { UUID } from "../../../../typings/global";
import { ProductFinancialSector } from "../../../product/enums";
import { ProductBase } from "../../../product/types";
import { formatProductName } from "../../../product/utils";
import { EnumerationType } from "../../enums";
import { FilterFunction } from "../../types";

interface Props {
  formItemProps: FormItemProps;
  selectProps?: SelectProps<string>;
  optionsProps?: ProductOptionsProps;
}

interface ProductOptionsProps {
  selectedProductId?: UUID;
  selectedInTree?: ProductOptionsSelectedInTreeProps;
  institutionId?: UUID;
  productGroupId?: UUID;
  filterSectors?: ProductFinancialSector[];
  groupByProductGroup?: boolean;
  hideAll?: boolean;
  filter?: FilterFunction<ProductBase>;
  onChange?: (product: ProductBase) => void;
}

interface ProductOptionsSelectedInTreeProps {
  selectedProductId?: UUID;
  condition: boolean;
}

const ProductSelect = ({ formItemProps, selectProps, optionsProps }: Props) => {
  const productsEnums = useEnumeration(EnumerationType.PRODUCTS);
  const productGroupsEnums = useEnumeration(EnumerationType.PRODUCT_GROUPS);

  const resolveProductSelectOptions = () => {
    if (optionsProps?.hideAll) {
      return [];
    }

    let finalProducts = [...productsEnums];

    if (optionsProps?.institutionId) {
      finalProducts = finalProducts.filter((product) => product.institutionIds.includes(optionsProps.institutionId!));
    }

    if (optionsProps?.productGroupId) {
      finalProducts = finalProducts.filter((product) => product.productGroupId === optionsProps.productGroupId);
    }

    if (optionsProps?.filterSectors?.length) {
      finalProducts = finalProducts.filter(
        (product) => product.sector && optionsProps?.filterSectors?.includes(product.sector),
      );
    }

    if (optionsProps?.filter) {
      finalProducts = finalProducts.filter(optionsProps.filter);
    }

    if (
      optionsProps?.selectedProductId &&
      !finalProducts.some((product) => product.id === optionsProps?.selectedProductId)
    ) {
      const selectedProduct = productsEnums.find((product) => product.id === optionsProps?.selectedProductId);

      if (selectedProduct) {
        finalProducts.unshift(selectedProduct);
      }
    }

    if (
      optionsProps?.selectedInTree?.condition &&
      optionsProps.selectedInTree.selectedProductId &&
      !finalProducts.some((product) => product.id === optionsProps?.selectedInTree?.selectedProductId)
    ) {
      const selectedProduct = productsEnums.find(
        (product) => product.id === optionsProps.selectedInTree?.selectedProductId,
      );

      if (selectedProduct) {
        finalProducts.unshift(selectedProduct);
      }
    }

    return finalProducts.sort((a, b) => a.name.localeCompare(b.name, DEFAULT_LOCALE, { sensitivity: "accent" }));
  };

  const onChange = (productId: UUID, option: any) => {
    selectProps?.onChange?.(productId, option);
    const product = productsEnums.find((product) => product.id === productId);

    if (product) {
      optionsProps?.onChange?.(product);
    }
  };

  if (optionsProps?.groupByProductGroup) {
    const groupsMap = groupBy(resolveProductSelectOptions(), (product) => product.productGroupId);
    const groups = Object.keys(groupsMap)
      .map((groupId) => ({
        ...productGroupsEnums.find((group) => group.id === groupId),
        products: groupsMap[groupId] as ProductBase[],
      }))
      .sort((a, b) => {
        const nameA = a.name ?? "";
        const nameB = b.name ?? "";

        return nameA.localeCompare(nameB, DEFAULT_LOCALE, { sensitivity: "accent" });
      });

    return (
      <Form.Item {...formItemProps}>
        <Select
          {...selectStandardProps}
          {...selectProps}
          onChange={onChange}
          options={groups.map((group) => ({
            label: group.name,
            options: group.products.map((p) => ({ value: p.id, label: formatProductName(p) })),
          }))}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item {...formItemProps}>
      <Select
        {...selectStandardProps}
        {...selectProps}
        onChange={onChange}
        options={resolveProductSelectOptions().map((product) => ({
          value: product.id,
          label: formatProductName(product),
        }))}
      />
    </Form.Item>
  );
};

export default ProductSelect;
